import { Children } from "react";
import { cn } from "@repo/ui/utils";
import { Carousel, CarouselContent, CarouselItem } from "@repo/ui/carousel";
import type { ListingCardContainerProps } from "./listing-card-container.types";

const desktopColSpan = {
  2: "desktop:col-span-6",
  3: "desktop:col-span-4",
  4: "desktop:col-span-3",
} as const;

export function ListingCardContainer({
  children,
  columns = 3,
  carouselOnMobile = true,
  rowGap = "md",
  className,
}: ListingCardContainerProps) {
  const items = Children.toArray(children);
  const gridWrapper = cn(
    "container-grid",
    rowGap === "lg" && "desktop:gap-y-12",
  );

  if (!carouselOnMobile) {
    return (
      <div
        data-testid="listing-card-container"
        className={cn(gridWrapper, className)}
      >
        {items.map((child, index) => (
          <div
            key={index}
            className={cn(
              "col-span-4 tablet:col-span-4",
              desktopColSpan[columns],
            )}
          >
            {child}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="listing-card-container" className={className}>
      {/* Tablet+: grid (2 cols on tablet, columns prop on desktop) */}
      <div className="hidden tablet:block">
        <div className={gridWrapper}>
          {items.map((child, index) => (
            <div
              key={index}
              className={cn("tablet:col-span-4", desktopColSpan[columns])}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: Carousel */}
      <div className="tablet:hidden">
        <Carousel align="start">
          <CarouselContent viewportClassName="pl-4" className="-ml-3">
            {items.map((child, index) => (
              <CarouselItem
                key={index}
                className="basis-[min(323px,85vw)] pl-3"
              >
                {child}
              </CarouselItem>
            ))}
            {/* Trailing spacer so the last card isn't flush with the screen edge */}
            <div aria-hidden className="shrink-0 grow-0 basis-4" />
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  );
}
